import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { usePatchChangeShipping } from 'services/Data/CartService';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import { useRegions } from 'services/Data/RegionService';
import { translate } from '@acnb/CustomComponents/DropdownItem/Translate';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import TrackingNumberSection from './TrackingNumberSection';
import { CartStatusEnumValue } from 'enums/CartStatusEnum';

const getShippingDraft = cart => ({
  isDealerShippingAddress: cart?.isDealerShippingAddress || false,
  shippingAddress: cart?.shippingAddress ?? '',
  shippingCity: cart?.shippingCity ?? '',
  shippingRegion_Id: cart?.shippingRegion_Id ?? null,
  shippingPostalCode: cart?.shippingPostalCode ?? '',
});

const DeliveryCheckoutSection = ({ cart, isLoading, validator, isDisabled }) => {
  const { t } = useTranslation();

  const { mutate: changeShipping, isPending: changingShipping } = usePatchChangeShipping();
  const { data: regions, isLoading: gettingRegions } = useRegions();
  const [shippingDraft, setShippingDraft] = useState(() => getShippingDraft(cart));

  useEffect(() => {
    setShippingDraft(getShippingDraft(cart));
  }, [
    cart?.isDealerShippingAddress,
    cart?.shippingAddress,
    cart?.shippingCity,
    cart?.shippingRegion_Id,
    cart?.shippingPostalCode,
  ]);

  const updateShippingDraft = (field, value) => {
    setShippingDraft(current => ({
      ...current,
      [field]: value,
    }));
  };

  const submitShippingChange = (field, value) => {
    if (!cart) {
      return;
    }

    changeShipping({
      ...cart,
      ...shippingDraft,
      [field]: value,
    });
  };

  return (
    <SkeletonWrapper
      isLoading={isLoading || changingShipping || gettingRegions}
      component={
        <GridContainer>
          <GridItem xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={({ target: { checked } }) => {
                    updateShippingDraft('isDealerShippingAddress', checked);
                    submitShippingChange('isDealerShippingAddress', checked);
                  }}
                  checked={shippingDraft.isDealerShippingAddress}
                  disabled={!cart || isDisabled || cart.isDemo}
                />
              }
              label={t('isDealerShippingAddress')}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingAddress')}
              id="shippingAddress"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingAddress'), shippingDraft.shippingAddress, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  submitShippingChange('shippingAddress', value);
                },
                onChange: ({ target: { value } }) => updateShippingDraft('shippingAddress', value),
                disabled: !cart || isDisabled || shippingDraft.isDealerShippingAddress,
                value: shippingDraft.shippingAddress,
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingCity')}
              id="shippingCity"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingCity'), shippingDraft.shippingCity, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  submitShippingChange('shippingCity', value);
                },
                onChange: ({ target: { value } }) => updateShippingDraft('shippingCity', value),
                disabled: !cart || isDisabled || shippingDraft.isDealerShippingAddress,
                value: shippingDraft.shippingCity,
              }}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomAutocomplete
              labelText={t('regions')}
              options={regions || []}
              formControlProps={{
                fullWidth: true,
              }}
              disabled={!cart || isDisabled || shippingDraft.isDealerShippingAddress}
              error={validator.current.message(t('region'), shippingDraft.shippingRegion_Id, `required`)}
              autocompleteProps={{
                onChange: (event, value) => {
                  updateShippingDraft('shippingRegion_Id', value?.id ?? null);
                  submitShippingChange('shippingRegion_Id', value?.id ?? null);
                },
                disableClearable: true,
                isOptionEqualToValue: (option, value) => option?.id === value,
                value: shippingDraft.shippingRegion_Id,
                getOptionLabel: option => {
                  let item = regions?.find(se => se?.id === option) || option;

                  return (
                    (item &&
                      `${translate({
                        label: item?.label,
                        t,
                      })}`) ||
                    ''
                  );
                },
                filterOptions: (options, state) => {
                  var searchTerms = state.inputValue.toLowerCase().split(' ');
                  var filteredOptions = options.filter(option =>
                    searchTerms.every(searchTerm =>
                      translate({ label: option.label, t })
                        ?.toLowerCase()
                        .includes(searchTerm),
                    ),
                  );

                  return filteredOptions;
                },
                renderOption: (props, option, { inputValue }) => {
                  const result = getHighlightParts(`${translate({ label: option.label, t })}`, inputValue);

                  return (
                    <div {...props} key={option.id}>
                      {result}
                    </div>
                  );
                },
                renderInput: params => (
                  <TextField
                    {...params}
                    label={t('regions')}
                    variant="outlined"
                    error={!!validator.current.message(t('region'), shippingDraft.shippingRegion_Id, 'required')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                      },
                    }}
                  />
                ),
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingPostalCode')}
              id="shippingPostalCode"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingPostalCode'), shippingDraft.shippingPostalCode, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  submitShippingChange('shippingPostalCode', value);
                },
                onChange: ({ target: { value } }) => updateShippingDraft('shippingPostalCode', value),
                disabled: !cart || isDisabled || shippingDraft.isDealerShippingAddress,
                value: shippingDraft.shippingPostalCode,
              }}
            />
          </GridItem>

          {cart?.status > CartStatusEnumValue.Active && (
            <GridItem xs={12}>
              <TrackingNumberSection cart={cart} isCartLoading={isLoading} />
            </GridItem>
          )}
        </GridContainer>
      }
    />
  );
};

export default DeliveryCheckoutSection;
